import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from 'react';

// Legacy ChatType for backward compatibility during migration
export type ChatType = 'assistant' | 'tenant' | 'sms' | 'whatsapp';

// Peer-based architecture: All conversations are with "peers"
export type PeerType = 'ai' | 'tenant' | 'service_desk'; // service_desk = group chat where multiple agents can respond
export type PeerId = string; // 'assistant' for AI, tenant ID for tenants, 'service_desk:{orgId}' for service desk chats

export interface Peer {
  id: PeerId;
  type: PeerType;
  name: string;
  icon?: string; // Optional icon identifier
  metadata?: Record<string, any>; // Additional peer-specific data (phone, email, etc.)
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tenant' | 'agent'; // 'agent' for service desk agents
  content: string;
  timestamp: number;
  read: boolean;
  senderId?: string;
  recipientId?: string;
  peerId?: PeerId; // Which peer this message is associated with
  peerType?: PeerType; // Type of peer (for routing)
  agentId?: string; // For service desk: which agent sent/received this message
  agentName?: string; // For service desk: display name of the agent
}

export interface ConversationItem {
  id: string; // Same as peerId
  peerId: PeerId;
  peerType: PeerType;
  name: string;
  lastMessage?: string;
  lastMessageTime?: number;
  unreadCount: number;
  isActive: boolean;
  // Legacy fields for backward compatibility
  type?: ChatType;
  tenantId?: string;
  tenantName?: string;
}

interface ChatSession {
  messages: Message[];
  unreadCount: number;
  peerId: PeerId; // Which peer this session is for
  // For service desk: track which agents are currently viewing/participating
  activeAgents?: Set<string>; // Set of agent user IDs currently in this conversation
  lastAgentActivity?: Record<string, number>; // Map of agentId -> last activity timestamp
}

interface ChatContextType {
  // Chat visibility
  isChatOpen: boolean;
  activePeerId: PeerId | null; // Currently active peer (replaces chatType + activeTenantId)
  currentOrganizationId: number | null;
  
  // Legacy fields for backward compatibility
  chatType: ChatType;
  activeTenantId: string | null;
  
  // Chat actions (peer-based)
  toggleChat: (peerId?: PeerId) => void;
  openChat: (peerId?: PeerId) => void;
  closeChat: () => void;
  
  // Legacy chat actions (for backward compatibility)
  toggleChatLegacy: (type?: ChatType, tenantId?: string) => void;
  openChatLegacy: (type?: ChatType, tenantId?: string) => void;
  
  // Message management (peer-based)
  messages: Message[];
  addMessage: (message: Omit<Message, 'id' | 'timestamp' | 'read'>, peerId?: PeerId) => string;
  updateMessage: (id: string, content: string | ((prev: string) => string), peerId?: PeerId) => void;
  clearMessages: (peerId?: PeerId) => void;
  
  // Legacy message management (for backward compatibility)
  addMessageLegacy: (message: Omit<Message, 'id' | 'timestamp' | 'read'>, type?: ChatType, tenantId?: string) => string;
  updateMessageLegacy: (id: string, content: string | ((prev: string) => string), type?: ChatType) => void;
  clearMessagesLegacy: (type?: ChatType) => void;
  
  // Unread counts
  unreadCount: number;
  resetUnreadCount: (peerId?: PeerId) => void;
  
  // Legacy unread counts
  unreadCounts: Record<ChatType, number>;
  resetUnreadCountLegacy: (type?: ChatType) => void;
  
  // Peer management
  peers: Record<PeerId, Peer>; // All known peers
  addPeer: (peer: Peer) => void;
  getPeer: (peerId: PeerId) => Peer | undefined;
  
  // Organization management
  setCurrentOrganization: (orgId: number | null) => void;
  
  // Conversation management (peer-based)
  conversations: ConversationItem[];
  switchToConversation: (peerId: PeerId) => void;
  
  // Legacy conversation management
  setActiveTenant: (tenantId: string | null) => void;
  getTenantMessages: (tenantId: string) => Message[];
  addTenantConversation: (tenantId: string, tenantName: string) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Helper to generate a unique chat session key (now peer-based)
const getChatSessionKey = (peerId: PeerId): string => {
  return peerId;
};

// Helper to convert legacy ChatType + tenantId to PeerId
const chatTypeToPeerId = (type: ChatType, tenantId?: string | null): PeerId => {
  if (type === 'assistant') return 'assistant';
  if (type === 'tenant' && tenantId) return `tenant:${tenantId}`;
  if (type === 'sms' && tenantId) return `sms:${tenantId}`;
  if (type === 'whatsapp' && tenantId) return `whatsapp:${tenantId}`;
  return type;
};

// Helper to get PeerType from PeerId
const getPeerTypeFromId = (peerId: PeerId): PeerType => {
  if (peerId === 'assistant') return 'ai';
  if (peerId.startsWith('tenant:')) return 'tenant';
  if (peerId.startsWith('sms:')) return 'tenant'; // SMS is also a tenant peer
  if (peerId.startsWith('whatsapp:')) return 'tenant'; // WhatsApp is also a tenant peer
  if (peerId.startsWith('service_desk:')) return 'service_desk';
  return 'tenant'; // Default fallback
};

// Helper to get service desk peer ID for an organization
export const getServiceDeskPeerId = (organizationId: number): PeerId => {
  return `service_desk:${organizationId}`;
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activePeerId, setActivePeerId] = useState<PeerId | null>('assistant'); // Default to AI assistant
  
  // Legacy state for backward compatibility
  const [chatType, setChatType] = useState<ChatType>('assistant');
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  
  const [currentOrganizationId, setCurrentOrganizationId] = useState<number | null>(null);
  
  // Peer registry: All known peers (AI, tenants, broadcasts)
  const [peers, setPeers] = useState<Record<PeerId, Peer>>({
    'assistant': {
      id: 'assistant',
      type: 'ai',
      name: 'AI Assistant',
      icon: 'robot'
    }
  });
  
  // Chat sessions: One per peer
  const [chatSessions, setChatSessions] = useState<Record<string, ChatSession>>({
    'assistant': { messages: [], unreadCount: 0, peerId: 'assistant' }
  });
  
  // Legacy tenant conversations (for backward compatibility)
  const [tenantConversations, setTenantConversations] = useState<Record<string, { name: string; lastMessage?: string; lastMessageTime?: number }>>({});
  
  // Sync activePeerId with legacy chatType/activeTenantId
  useEffect(() => {
    if (activePeerId === 'assistant') {
      setChatType('assistant');
      setActiveTenantId(null);
    } else if (activePeerId?.startsWith('tenant:')) {
      const tenantId = activePeerId.replace('tenant:', '');
      setChatType('tenant');
      setActiveTenantId(tenantId);
    } else if (activePeerId?.startsWith('sms:')) {
      const tenantId = activePeerId.replace('sms:', '');
      setChatType('sms');
      setActiveTenantId(tenantId);
    } else if (activePeerId?.startsWith('whatsapp:')) {
      const tenantId = activePeerId.replace('whatsapp:', '');
      setChatType('whatsapp');
      setActiveTenantId(tenantId);
    }
  }, [activePeerId]);
  
  // Sync legacy chatType/activeTenantId with activePeerId
  useEffect(() => {
    const peerId = chatTypeToPeerId(chatType, activeTenantId);
    if (peerId !== activePeerId) {
      setActivePeerId(peerId);
    }
  }, [chatType, activeTenantId]);

  // Get the current chat session (peer-based)
  const currentSessionKey = useMemo(() => {
    return activePeerId ? getChatSessionKey(activePeerId) : 'assistant';
  }, [activePeerId]);

  // Get messages for the current chat session
  const currentMessages = useMemo(() => {
    return chatSessions[currentSessionKey]?.messages || [];
  }, [chatSessions, currentSessionKey]);

  // Calculate total unread count
  const totalUnreadCount = useMemo(() => {
    return Object.values(chatSessions).reduce((sum, session) => sum + session.unreadCount, 0);
  }, [chatSessions]);

  // Get unread counts by chat type (legacy)
  const unreadCounts = useMemo((): Record<ChatType, number> => {
    return {
      assistant: chatSessions['assistant']?.unreadCount || 0,
      tenant: Object.keys(chatSessions)
        .filter(key => key.startsWith('tenant:'))
        .reduce((sum, key) => sum + (chatSessions[key]?.unreadCount || 0), 0),
      sms: Object.keys(chatSessions)
        .filter(key => key.startsWith('sms:'))
        .reduce((sum, key) => sum + (chatSessions[key]?.unreadCount || 0), 0),
      whatsapp: Object.keys(chatSessions)
        .filter(key => key.startsWith('whatsapp:'))
        .reduce((sum, key) => sum + (chatSessions[key]?.unreadCount || 0), 0),
    };
  }, [chatSessions]);

  // Get all conversations for the sidebar (peer-based)
  const conversations = useMemo(() => {
    const conversationList: ConversationItem[] = [];
    
    // Add all peers that have sessions or are registered
    Object.values(peers).forEach(peer => {
      const session = chatSessions[peer.id];
      const lastMessage = session?.messages[session.messages.length - 1];
      
      // Every registered peer stays listed regardless of message history or
      // which conversation is currently open — `peers` only ever holds
      // peers someone deliberately added (via addPeer/addTenantConversation
      // etc.), so there's no need to additionally gate on hasMessages or
      // isCurrentlyActive. Gating on those was the bug: switching the
      // active conversation to one that happened to have zero messages so
      // far (or hadn't been the initially-active one) made every OTHER
      // peer vanish from the sidebar the instant it stopped being active,
      // since hasMessages was the only thing keeping it visible.
      const isCurrentlyActive = activePeerId === peer.id;
      conversationList.push({
        id: peer.id,
        peerId: peer.id,
        peerType: peer.type,
        name: peer.name,
        lastMessage: lastMessage?.content,
        lastMessageTime: lastMessage?.timestamp,
        unreadCount: session?.unreadCount || 0,
        isActive: isCurrentlyActive,
        // Legacy fields for backward compatibility
        type: peer.id === 'assistant' ? 'assistant' : peer.id.startsWith('sms:') ? 'sms' : 'tenant',
        tenantId: peer.id.startsWith('tenant:') || peer.id.startsWith('sms:')
          ? peer.id.replace(/^(tenant|sms):/, '')
          : undefined,
        tenantName: peer.type === 'tenant' ? peer.name : undefined,
      });
    });
    
    // Also add legacy tenant conversations that might not be in peers yet
    Object.entries(tenantConversations).forEach(([tenantId, tenantData]) => {
      const peerId = `tenant:${tenantId}`;
      if (!peers[peerId]) {
        const session = chatSessions[peerId];
        const hasMessages = session?.messages && session.messages.length > 0;
        const isCurrentlyActive = activePeerId === peerId;
        
        if (hasMessages || isCurrentlyActive) {
          conversationList.push({
            id: peerId,
            peerId,
            peerType: 'tenant',
            name: tenantData.name,
            lastMessage: session?.messages[session?.messages.length - 1]?.content || tenantData.lastMessage,
            lastMessageTime: session?.messages[session?.messages.length - 1]?.timestamp || tenantData.lastMessageTime,
            unreadCount: session?.unreadCount || 0,
            isActive: isCurrentlyActive,
            // Legacy fields
            type: 'tenant',
            tenantId,
            tenantName: tenantData.name,
          });
        }
      }
    });
    
    // Sort by last message time (most recent first)
    return conversationList.sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));
  }, [chatSessions, peers, tenantConversations, activePeerId]);

  // Peer-based toggle chat
  const toggleChat = useCallback((peerId?: PeerId) => {
    const targetPeerId = peerId || activePeerId || 'assistant';
    setActivePeerId(targetPeerId);
    
    const newState = !isChatOpen;
    setIsChatOpen(newState);
    
    if (newState) {
      resetUnreadCount(targetPeerId);
    }
  }, [isChatOpen, activePeerId]);

  // Legacy toggle chat (for backward compatibility)
  const toggleChatLegacy = useCallback((type?: ChatType, tenantId?: string) => {
    const peerId = chatTypeToPeerId(type || chatType, tenantId || activeTenantId);
    toggleChat(peerId);
  }, [chatType, activeTenantId, toggleChat]);

  // Peer-based open chat
  const openChat = useCallback((peerId?: PeerId) => {
    const targetPeerId = peerId || activePeerId || 'assistant';
    setActivePeerId(targetPeerId);
    setIsChatOpen(true);
    resetUnreadCount(targetPeerId);
  }, [activePeerId]);

  // Legacy open chat (for backward compatibility)
  const openChatLegacy = useCallback((type?: ChatType, tenantId?: string) => {
    const peerId = chatTypeToPeerId(type || chatType, tenantId || activeTenantId);
    openChat(peerId);
  }, [chatType, activeTenantId, openChat]);

  // Close the chat
  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  // Peer-based add message
  const addMessage = useCallback((
    message: Omit<Message, 'id' | 'timestamp' | 'read'>,
    peerId?: PeerId
  ): string => {
    // Determine which peer this message belongs to
    let targetPeerId: PeerId = peerId || activePeerId || 'assistant';
    
    // If message has senderId and role is 'tenant', determine peer from sender
    if (message.role === 'tenant' && message.senderId) {
      targetPeerId = `tenant:${message.senderId}`;
    }
    
    // Ensure peer exists
    if (!peers[targetPeerId] && targetPeerId !== 'assistant') {
      const peerType = getPeerTypeFromId(targetPeerId);
      const peerName = message.senderId 
        ? `Tenant ${message.senderId}` 
        : targetPeerId === 'broadcast' 
          ? 'Broadcast' 
          : 'Unknown';
      
      setPeers(prev => ({
        ...prev,
        [targetPeerId]: {
          id: targetPeerId,
          type: peerType,
          name: peerName,
        }
      }));
    }
    
    const sessionKey = getChatSessionKey(targetPeerId);
    const newMessage: Message = {
      ...message,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      read: isChatOpen && activePeerId === targetPeerId,
      peerId: targetPeerId,
      peerType: getPeerTypeFromId(targetPeerId),
      // For service desk messages, include agent info if available
      ...(targetPeerId.startsWith('service_desk:') && currentAgentId && {
        agentId: currentAgentId,
        agentName: currentAgentName || undefined,
        role: message.role === 'user' ? 'agent' : message.role // User messages in service desk are from agents
      }),
    };
    
    // Update tenant conversations (legacy support)
    if (targetPeerId.startsWith('tenant:') || targetPeerId.startsWith('sms:')) {
      const tenantId = targetPeerId.replace(/^(tenant|sms):/, '');
      setTenantConversations(prev => {
        const existing = prev[tenantId];
        if (!existing) {
          return {
            ...prev,
            [tenantId]: {
              name: peers[targetPeerId]?.name || `Tenant ${tenantId}`,
              lastMessage: newMessage.content,
              lastMessageTime: newMessage.timestamp
            }
          };
        } else {
          return {
            ...prev,
            [tenantId]: {
              ...existing,
              lastMessage: newMessage.content,
              lastMessageTime: newMessage.timestamp
            }
          };
        }
      });
    }
    
    setChatSessions(prev => ({
      ...prev,
      [sessionKey]: {
        ...prev[sessionKey] || { messages: [], unreadCount: 0, peerId: targetPeerId },
        messages: [...(prev[sessionKey]?.messages || []), newMessage],
        peerId: targetPeerId,
        unreadCount: message.role === 'user' 
          ? (prev[sessionKey]?.unreadCount || 0) // Don't increment for user's own messages
          : (isChatOpen && activePeerId === targetPeerId) 
            ? 0 
            : (prev[sessionKey]?.unreadCount || 0) + 1,
      }
    }));
    
    return newMessage.id;
  }, [isChatOpen, activePeerId, peers]);
  
  // Legacy add message (for backward compatibility)
  const addMessageLegacy = useCallback((
    message: Omit<Message, 'id' | 'timestamp' | 'read'>,
    type?: ChatType,
    tenantId?: string
  ): string => {
    const peerId = chatTypeToPeerId(type || chatType, tenantId || activeTenantId);
    return addMessage(message, peerId);
  }, [chatType, activeTenantId, addMessage]);

  // Peer-based update message
  const updateMessage = useCallback((
    id: string, 
    content: string | ((prev: string) => string),
    peerId?: PeerId
  ) => {
    const targetPeerId = peerId || activePeerId || 'assistant';
    const sessionKey = getChatSessionKey(targetPeerId);
    
    setChatSessions(prev => {
      const session = prev[sessionKey];
      if (!session) return prev;
      
      return {
        ...prev,
        [sessionKey]: {
          ...session,
          messages: session.messages.map(msg => 
            msg.id === id 
              ? { 
                  ...msg, 
                  content: typeof content === 'function' 
                    ? (content as (prev: string) => string)(msg.content) 
                    : content,
                  timestamp: Date.now()
                }
              : msg
          )
        }
      };
    });
  }, [activePeerId]);
  
  // Legacy update message (for backward compatibility)
  const updateMessageLegacy = useCallback((
    id: string, 
    content: string | ((prev: string) => string),
    type?: ChatType
  ) => {
    const peerId = chatTypeToPeerId(type || chatType, activeTenantId);
    updateMessage(id, content, peerId);
  }, [chatType, activeTenantId, updateMessage]);

  // Peer-based clear messages
  const clearMessages = useCallback((peerId?: PeerId) => {
    const targetPeerId = peerId || activePeerId || 'assistant';
    const sessionKey = getChatSessionKey(targetPeerId);
    
    setChatSessions(prev => ({
      ...prev,
      [sessionKey]: {
        ...prev[sessionKey] || { messages: [], unreadCount: 0, peerId: targetPeerId },
        messages: [],
        unreadCount: 0,
        peerId: targetPeerId
      }
    }));
  }, [activePeerId]);
  
  // Legacy clear messages (for backward compatibility)
  const clearMessagesLegacy = useCallback((type?: ChatType) => {
    const peerId = chatTypeToPeerId(type || chatType, activeTenantId);
    clearMessages(peerId);
  }, [chatType, activeTenantId, clearMessages]);

  // Peer-based reset unread count
  const resetUnreadCount = useCallback((peerId?: PeerId) => {
    const targetPeerId = peerId || activePeerId || 'assistant';
    const sessionKey = getChatSessionKey(targetPeerId);
    
    setChatSessions(prev => {
      const currentSession = prev[sessionKey];
      if (!currentSession) return prev;
      
      return {
        ...prev,
        [sessionKey]: {
          ...currentSession,
          unreadCount: 0
        }
      };
    });
  }, [activePeerId]);
  
  // Legacy reset unread count (for backward compatibility)
  const resetUnreadCountLegacy = useCallback((type?: ChatType) => {
    const peerId = chatTypeToPeerId(type || chatType, activeTenantId);
    resetUnreadCount(peerId);
  }, [chatType, activeTenantId, resetUnreadCount]);

  // Add a peer to the registry
  const addPeer = useCallback((peer: Peer) => {
    setPeers(prev => ({
      ...prev,
      [peer.id]: peer
    }));
    
    // Initialize session for this peer if it doesn't exist
    const sessionKey = getChatSessionKey(peer.id);
    setChatSessions(prev => ({
      ...prev,
      [sessionKey]: prev[sessionKey] || { 
        messages: [], 
        unreadCount: 0,
        peerId: peer.id
      }
    }));
  }, []);

  // Get a peer by ID
  const getPeer = useCallback((peerId: PeerId): Peer | undefined => {
    return peers[peerId];
  }, [peers]);

  // Set current organization
  const setCurrentOrganization = useCallback((orgId: number | null) => {
    setCurrentOrganizationId(orgId);
  }, []);

  // Switch to a specific conversation (peer-based)
  const switchToConversation = useCallback((peerId: PeerId) => {
    setActivePeerId(peerId);
    setIsChatOpen(true);
    resetUnreadCount(peerId);
  }, [resetUnreadCount]);

  // Legacy methods for backward compatibility
  const setActiveTenant = useCallback((tenantId: string | null) => {
    setActiveTenantId(tenantId);
    if (tenantId) {
      const peerId = `tenant:${tenantId}`;
      addPeer({
        id: peerId,
        type: 'tenant',
        name: `Tenant ${tenantId}`,
      });
    }
  }, [addPeer]);

  // Get messages for a specific tenant (legacy)
  const getTenantMessages = useCallback((tenantId: string): Message[] => {
    const peerId = `tenant:${tenantId}`;
    const sessionKey = getChatSessionKey(peerId);
    return chatSessions[sessionKey]?.messages || [];
  }, [chatSessions]);

  // Add a new tenant conversation (legacy)
  const addTenantConversation = useCallback((tenantId: string, tenantName: string) => {
    const peerId = `tenant:${tenantId}`;
    
    // Add to peers registry
    addPeer({
      id: peerId,
      type: 'tenant',
      name: tenantName,
    });
    
    // Also update legacy tenant conversations
    setTenantConversations(prev => ({
      ...prev,
      [tenantId]: {
        name: tenantName,
        lastMessage: 'Conversation started',
        lastMessageTime: Date.now()
      }
    }));
  }, [addPeer]);

  // Track current agent for service desk (used in addMessage for service desk messages)
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null);
  const [currentAgentName, setCurrentAgentName] = useState<string | null>(null); // Used in addMessage for service desk

  // Join service desk conversation (for multi-agent support)
  const joinServiceDesk = useCallback((organizationId: number, agentId: string, agentName: string) => {
    const peerId = getServiceDeskPeerId(organizationId);
    
    // Set current agent
    setCurrentAgentId(agentId);
    setCurrentAgentName(agentName);
    
    // Ensure service desk peer exists
    if (!peers[peerId]) {
      addPeer({
        id: peerId,
        type: 'service_desk',
        name: `Service Desk (Org ${organizationId})`,
        icon: 'service-desk',
        metadata: { organizationId }
      });
    }
    
    // Add agent to active agents list
    setChatSessions(prev => {
      const existingSession = prev[peerId];
      const session: ChatSession = existingSession || { 
        messages: [], 
        unreadCount: 0, 
        peerId, 
        activeAgents: new Set<string>(), 
        lastAgentActivity: {} 
      };
      const activeAgents = new Set<string>(session.activeAgents || []);
      activeAgents.add(agentId);
      
      const lastAgentActivity = { ...(session.lastAgentActivity || {}), [agentId]: Date.now() };
      
      return {
        ...prev,
        [peerId]: {
          ...session,
          activeAgents,
          lastAgentActivity
        }
      };
    });
  }, [peers, addPeer]);

  // Leave service desk conversation
  const leaveServiceDesk = useCallback((organizationId: number, agentId: string) => {
    const peerId = getServiceDeskPeerId(organizationId);
    
    // Remove agent from active agents list
    setChatSessions(prev => {
      const session = prev[peerId];
      if (!session) return prev;
      
      const activeAgents = new Set<string>(session.activeAgents || []);
      activeAgents.delete(agentId);
      
      const lastAgentActivity = { ...(session.lastAgentActivity || {}), [agentId]: Date.now() };
      
      return {
        ...prev,
        [peerId]: {
          ...session,
          activeAgents,
          lastAgentActivity
        }
      };
    });
    
    // Clear current agent if it's this agent
    if (currentAgentId === agentId) {
      setCurrentAgentId(null);
      setCurrentAgentName(null);
    }
  }, [currentAgentId]);

  // Get active agents in service desk
  const getServiceDeskActiveAgents = useCallback((organizationId: number): Array<{ id: string; name: string }> => {
    const peerId = getServiceDeskPeerId(organizationId);
    const session = chatSessions[peerId];
    
    if (!session || !session.activeAgents) return [];
    
    // Return active agents with their names (would need to be stored in session or fetched)
    // For now, return IDs - this can be enhanced to include names from a lookup
    return Array.from(session.activeAgents as Set<string>).map((id: string) => ({
      id,
      name: session.lastAgentActivity?.[id] ? `Agent ${id}` : 'Unknown Agent' // Placeholder - should fetch actual names
    }));
  }, [chatSessions]);

  // Context value
  const contextValue = useMemo(() => ({
    // Peer-based state
    isChatOpen,
    activePeerId,
    currentOrganizationId,
    
    // Legacy state (for backward compatibility)
    chatType,
    activeTenantId,
    
    // Peer-based actions
    toggleChat,
    openChat,
    closeChat,
    addMessage,
    updateMessage,
    clearMessages,
    resetUnreadCount,
    switchToConversation,
    addPeer,
    getPeer,
    
    // Legacy actions (for backward compatibility)
    toggleChatLegacy,
    openChatLegacy,
    addMessageLegacy,
    updateMessageLegacy,
    clearMessagesLegacy,
    resetUnreadCountLegacy,
    setActiveTenant,
    getTenantMessages,
    addTenantConversation,
    
    // Shared state
    messages: currentMessages,
    unreadCount: totalUnreadCount,
    unreadCounts,
    peers,
    setCurrentOrganization,
    conversations,
  }), [
    isChatOpen,
    activePeerId,
    currentOrganizationId,
    chatType,
    activeTenantId,
    currentMessages,
    totalUnreadCount,
    unreadCounts,
    conversations,
    peers,
    toggleChat,
    openChat,
    closeChat,
    addMessage,
    updateMessage,
    clearMessages,
    resetUnreadCount,
    switchToConversation,
    addPeer,
    getPeer,
    toggleChatLegacy,
    openChatLegacy,
    addMessageLegacy,
    updateMessageLegacy,
    clearMessagesLegacy,
    resetUnreadCountLegacy,
    setActiveTenant,
    getTenantMessages,
    addTenantConversation,
    setCurrentOrganization,
    joinServiceDesk,
    leaveServiceDesk,
    getServiceDeskActiveAgents,
  ]);

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

/** Like useChat, but returns undefined outside a ChatProvider (for optional entry points). */
export const useOptionalChat = (): ChatContextType | undefined => useContext(ChatContext);
